import React, { useState, useEffect, useRef } from 'react';
import { MapPin, ChevronDown, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LocationSwitcher = ({ onLocationChange }) => {
  const [locations, setLocations] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const onChange = useRef(onLocationChange);
  onChange.current = onLocationChange;

  const token = localStorage.getItem('token');
  const orgId = localStorage.getItem('current_organization_id');

  useEffect(() => {
    let cancelled = false;
    setLocations([]);
    setCurrentLocation(null);
    const fetchLocations = async () => {
    // Don't call the tenant-scoped endpoint until we have both a token and an
    // organization id — otherwise it 401s (no auth yet) or 400s (no org header).
    if (!token || !orgId) {
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/locations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || ''
        }
      });
      if (res.ok && !cancelled) {
        const data = await res.json();
        if (cancelled || !Array.isArray(data)) return;
        setLocations(data);
        const saved = localStorage.getItem('current_location_id');
        const location = data.find(l => l.id === saved) || data[0];
        setCurrentLocation(location?.id || null);
        if (location) {
          localStorage.setItem('current_location_id', location.id);
          onChange.current?.(location);
          window.dispatchEvent(new CustomEvent('locationChanged', { detail: location }));
        } else {
          localStorage.removeItem('current_location_id');
        }
      }
    } catch (err) {
      console.error('Failed to fetch locations:', err);
    } finally {
      if (!cancelled) setLoading(false);
    }
    };
    fetchLocations();
    return () => { cancelled = true; };
  }, [token, orgId]);

  const handleLocationSelect = (location) => {
    if (loading || orgId !== localStorage.getItem('current_organization_id') ||
        token !== localStorage.getItem('token')) return;
    setCurrentLocation(location.id);
    localStorage.setItem('current_location_id', location.id);
    setIsOpen(false);
    if (onLocationChange) {
      onLocationChange(location);
    }
    // Trigger page refresh or context update
    window.dispatchEvent(new CustomEvent('locationChanged', { detail: location }));
  };

  const currentLocationData = locations.find(l => l.id === currentLocation);

  if (locations.length <= 1) {
    // Don't show switcher if only one location
    return null;
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <MapPin className="w-4 h-4 text-gray-500" />
        <span className="text-sm font-medium text-gray-700">
          {currentLocationData ? currentLocationData.name : 'Select Location'}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          ></div>
          <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">Loading...</div>
            ) : (
              <div className="py-2">
                {locations.map((location) => (
                  <button
                    key={location.id}
                    onClick={() => handleLocationSelect(location)}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center justify-between ${
                      currentLocation === location.id ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{location.name}</p>
                        {location.address && (
                          <p className="text-xs text-gray-500">
                            {location.address.city}, {location.address.postcode}
                          </p>
                        )}
                      </div>
                    </div>
                    {currentLocation === location.id && (
                      <Check className="w-4 h-4 text-blue-600" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default LocationSwitcher;
